import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Mail, Phone, ArrowLeft, RotateCcw, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { EmptyState } from "@/components/ui/empty-state";

export default function ArchivedClientList() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Fetch archived clients from Convex
  const archivedClients = useQuery(api.businesses.getArchivedClients) ?? [];
  const unarchiveClient = useMutation(api.businesses.unarchive);

  const filteredClients = archivedClients.filter(
    (client: any) =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.contactName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (client.contactEmail?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  const handleUnarchive = async (clientId: Id<"businesses">, clientName: string) => {
    try {
      await unarchiveClient({ id: clientId });
      toast.success(`${clientName} has been unarchived successfully`);
    } catch (error) {
      console.error("Failed to unarchive client:", error);
      toast.error("Failed to unarchive client");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/clients")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Archived Clients</h1>
            <p className="mt-1 text-gray-500">View and manage archived clients</p>
          </div>
        </div>
      </div>

      <Card className="p-6">
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search archived clients..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {filteredClients.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <Building2 className="mx-auto h-12 w-12" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">No archived clients found</h3>
            <p className="text-gray-500">
              {searchQuery
                ? "Try adjusting your search criteria"
                : "You haven't archived any clients yet"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Account Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client: any) => (
                <TableRow key={client._id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{client.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{client.accountType || "N/A"}</span>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div>{client.contactName || "N/A"}</div>
                      {(client.contactEmail || client.contactPhone) && (
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          {client.contactEmail && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {client.contactEmail}
                            </div>
                          )}
                          {client.contactPhone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {client.contactPhone}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        client.serviceStatus === "active"
                          ? "bg-green-100 text-green-700"
                          : client.serviceStatus === "pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {(client.serviceStatus || "Active").charAt(0).toUpperCase() +
                        (client.serviceStatus || "Active").slice(1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleUnarchive(client._id, client.name)}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Unarchive
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
